// Moving a café's players from another system (F-HQ-40, spec/14 §3.5): one organisation's moves, and starting one.
//
// A move belongs to one venue and names the platform the café is leaving. Every platform reads through the Clavian CSV
// for now (`@clavian/domain` `TEMPLATE_FOR_PLATFORM`), so the picker says so beside each name. Starting one opens its
// stepper page (`MigrationRunPage`).

import type { MigrationRun, MigrationSourcePlatform } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, SelectField, Table, type TableColumn, VisuallyHidden } from '@clavian/ui-web';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { TableSkeleton } from '../../ui';
import { LoadFailure } from '../../shell/LoadFailure';
import { NotPermitted, RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS } from '../sections';
import { useVenueList } from '../venues/queries';
import { useMigrationRuns, useStartMigration } from './queries';

import './migration.css';
import { HqHeading } from '../heading';

/**
 * The platforms a café can move from, as the contract lists them (`MIGRATION_SOURCE_PLATFORMS`; a test holds the two
 * equal). Copied rather than imported: the console's bundle never loads the contract's schemas.
 */
export const SOURCE_PLATFORMS: readonly MigrationSourcePlatform[] = ['ggleap', 'gizmo', 'senet', 'icafecloud', 'pancafe', 'smartlaunch', 'antamedia', 'handycafe', 'truecafe', 'other'];

/** An organisation's params, as HQ's links name them: plain strings (`src/hq/paths.ts`). */
export const organizationParams = (organizationId: string): Readonly<Record<string, string>> => ({ organizationId });

/** The tone of a move's status. */
export const RUN_TONE: Readonly<Record<MigrationRun['status'], 'neutral' | 'accent' | 'ok' | 'warn' | 'info'>> = {
  draft: 'neutral',
  ready: 'accent',
  importing: 'warn',
  completed: 'ok',
  undone: 'info',
};

/** A platform as the picker names it: every one reads through the Clavian CSV until it has an adapter of its own. */
export function usePlatformLabel(): (platform: MigrationSourcePlatform) => string {
  const { t } = useTranslation('hq');
  return (platform) => (platform === 'other' ? t('migration.platform.other') : t('migration.viaCsv', { platform: t(`migration.platform.${platform}`) }));
}

export function MigrationPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <MigrationsView />
    </RequirePlatformPermission>
  );
}

function MigrationsView() {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const params = useParams({ strict: false }) as { organizationId?: string };
  const organizationId = params.organizationId ?? '';
  const runs = useMigrationRuns(organizationId);
  const platformLabel = usePlatformLabel();
  const runParams = (migrationId: string): Readonly<Record<string, string>> => ({ organizationId, migrationId });

  if (runs.isError && problemCode(runs.error) === PROBLEM.forbidden) return <NotPermitted />;

  const columns: TableColumn<MigrationRun>[] = [
    { key: 'venue', header: t('migration.runs.column.venue'), cell: (run) => run.venueName },
    { key: 'from', header: t('migration.runs.column.from'), cell: (run) => t(`migration.platform.${run.sourcePlatform}`) },
    { key: 'status', header: t('migration.runs.column.status'), cell: (run) => <Badge tone={RUN_TONE[run.status]}>{t(`migration.status.${run.status}`)}</Badge> },
    { key: 'players', header: t('migration.runs.column.players'), numeric: true, cell: (run) => format.number(run.posted?.players ?? run.summary?.players ?? 0) },
    { key: 'started', header: t('migration.runs.column.started'), cell: (run) => format.dateTime(run.createdAt, { date: 'medium', time: 'short' }) },
    {
      key: 'open',
      header: <VisuallyHidden>{t('migration.runs.open')}</VisuallyHidden>,
      cell: (run) => (
        <Link to={HQ_PATHS.organizationMigrationRun} params={runParams(run.id)}>
          {t('migration.runs.open')}
        </Link>
      ),
    },
  ];

  return (
    <div className="console-page console-migration">
      <Link className="console-hq-back" to={HQ_PATHS.organization} params={organizationParams(organizationId)}>
        {t('migration.back')}
      </Link>
      <HqHeading title={t('migration.title')} tip={t('migration.intro')} />
      <StartMove organizationId={organizationId} platformLabel={platformLabel} />
      <section className="console-hq-panel console-migration__wide" aria-labelledby="console-migration-runs">
        <h2 id="console-migration-runs">{t('migration.runs.title')}</h2>
        {runs.data === undefined ? (
          runs.isError ? (
            <LoadFailure error={runs.error} onRetry={() => void runs.refetch()} />
          ) : (
            <TableSkeleton />
          )
        ) : (
          <Table columns={columns} rows={runs.data.data} rowKey={(run) => run.id} caption={t('migration.runs.caption')} captionHidden empty={t('migration.runs.none')} />
        )}
      </section>
    </div>
  );
}

function StartMove({ organizationId, platformLabel }: { readonly organizationId: string; readonly platformLabel: (platform: MigrationSourcePlatform) => string }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const navigate = useNavigate();
  const venues = useVenueList({ organizationId });
  const start = useStartMigration(organizationId);
  const venueOptions = useMemo(() => (venues.data?.pages ?? []).flatMap((page) => page.data).map((row) => ({ value: row.venue.id, label: row.venue.name })), [venues.data]);
  const [venueId, setVenueId] = useState<string | undefined>(undefined);
  const [platform, setPlatform] = useState<MigrationSourcePlatform>('truecafe');
  // The first venue until another is chosen (a select may report an empty value as it mounts).
  const chosenVenue = venueId !== undefined && venueId !== '' ? venueId : venueOptions[0]?.value;
  const busy = start.isError && problemCode(start.error) === PROBLEM.conflict;

  return (
    <section className="console-hq-panel" aria-labelledby="console-migration-start">
      <h2 id="console-migration-start">{t('migration.source.title')}</h2>
      {venues.data !== undefined && venueOptions.length === 0 ? (
        <p className="console-muted">{t('migration.source.noVenues')}</p>
      ) : (
        <form
          className="console-migration__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (chosenVenue === undefined || chosenVenue === '') return;
            start.mutate(
              { venueId: chosenVenue as never, sourcePlatform: platform },
              {
                onSuccess: (run) => {
                  const runParams: Readonly<Record<string, string>> = { organizationId, migrationId: run.id };
                  void navigate({ to: HQ_PATHS.organizationMigrationRun, params: runParams });
                },
              },
            );
          }}
        >
          <SelectField
            label={t('migration.source.venue')}
            description={t('migration.source.venueHint')}
            options={venueOptions}
            value={chosenVenue}
            onValueChange={setVenueId}
            disabled={venueOptions.length === 0}
          />
          <SelectField
            label={t('migration.source.platform')}
            description={t('migration.source.platformHint')}
            options={SOURCE_PLATFORMS.map((value) => ({ value, label: platformLabel(value) }))}
            value={platform}
            onValueChange={(value) => setPlatform(value as MigrationSourcePlatform)}
          />
          {busy ? (
            <p className="console-alert" role="alert">
              {t('migration.source.busy')}
            </p>
          ) : start.isError ? (
            <p className="console-alert" role="alert">
              {problems.text(start.error)}
            </p>
          ) : null}
          <div className="console-form__actions">
            <Button type="submit" busy={start.isPending} disabled={chosenVenue === undefined}>
              {t('migration.runs.start')}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
